import React, { useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Row, Col } from "reactstrap"
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    MarkerType,
    ConnectionMode
} from 'reactflow';
import { Copy, Plus, Trash2, RotateCcw, Target, MousePointer } from 'lucide-react';
import { useDarkMode } from 'context/DarkModeProvider';
import 'reactflow/dist/style.css';
import "../../assets/css/CreateYourOwn/CommonCreate.css"
import "../../assets/css/colors.css"
import { useLanguage } from 'context/LanguageProvider';
import CodeHighlighter from 'components/CodeHighlighter';



const CustomNode = ({ id, data, selected }) => {

    const nodeStyle = {
        padding: '12px 16px',
        borderRadius: '30px',
        border: selected ? '3px solid #506ce9ff' : '2px solid #667eea',
        background: selected
            ? 'linear-gradient(135deg, #506ce9ff, #334597ff)'
            : 'linear-gradient(135deg, #667eea, #764ba2)',
        color: 'white',
        fontSize: '14px',
        fontWeight: '600',
        minWidth: '60px',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative'
    };

    return (
        <div style={nodeStyle}>
            <Handle
                type="target"
                position={Position.Top}
                style={{ background: '#667eea', width: 2, height: 2, opacity: 0 }}
            />
            {data.label}
            <Handle
                type="source"
                position={Position.Bottom}
                style={{ background: '#667eea', width: 2, height: 2, opacity: 0 }}
            />
        </div>
    );
};


const nodeTypes = {
    custom: CustomNode,
};

const TreeBuilder = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [nodeCounter, setNodeCounter] = useState(1);
    const [nodeValue, setNodeValue] = useState('');
    const [mode, setMode] = useState('add');
    const [connectingFrom, setConnectingFrom] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('javascript');
    const [copied, setCopied] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [actionLog, setActionLog] = useState([]);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    // Handle node clicks
    const onNodeClick = useCallback((event, node) => {
        event.stopPropagation();

        if (mode === 'delete') {
            setNodes((nds) => nds.filter((n) => n.id !== node.id));
            setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));
            if (connectingFrom === node.id) {
                setConnectingFrom(null);
            }
            setActionLog(prev => [...prev, { type: 'deleteNode', value: node.data.label }]);
        }
        else if (mode === 'connect') {
            if (!connectingFrom) {
                setConnectingFrom(node.id);
            } else if (connectingFrom === node.id) {
                setConnectingFrom(null);
            } else {
                const alreadyHasParent = edges.some(e => e.target === node.id);
                if (alreadyHasParent) {
                    alert("Αυτός ο κόμβος έχει ήδη γονέα! Στο δέντρο κάθε κόμβος μπορεί να έχει μόνο έναν γονέα.");
                    setConnectingFrom(null);
                    return;
                }

                const fromNode = nodes.find(n => n.id === connectingFrom);

                const newEdge = {
                    id: `e${connectingFrom}-${node.id}`,
                    source: connectingFrom,
                    target: node.id,
                    type: 'default',
                    markerEnd: { type: MarkerType.ArrowClosed },
                    style: { strokeWidth: 2, stroke: '#667eea' },
                };

                setEdges((eds) => [...eds, newEdge]);
                setActionLog(prev => [...prev, {
                    type: 'connect',
                    from: fromNode?.data.label,
                    to: node.data.label
                }]);
                setConnectingFrom(null);
            }
        }
    }, [mode, connectingFrom, setNodes, setEdges, edges, nodes]);


    const onEdgeClick = useCallback((event, edge) => {
        if (mode === 'delete') {
            setEdges((eds) => eds.filter((e) => e.id !== edge.id));
            const fromNode = nodes.find(n => n.id === edge.source);
            const toNode = nodes.find(n => n.id === edge.target);
            setActionLog(prev => [...prev, {
                type: 'deleteEdge',
                from: fromNode?.data.label,
                to: toNode?.data.label
            }]);
        }
    }, [mode, setEdges, nodes]);

    // Add new node
    const addNode = useCallback(() => {
        const value = nodeValue.trim() || nodeCounter.toString();
        const newNode = {
            id: `node_${nodeCounter}`,
            type: 'custom',
            position: {
                x: 100 + (nodeCounter * 80) % 500,
                y: 100 + (Math.floor(nodeCounter / 6) * 150)
            },
            data: { label: value },
        };

        setNodes((nds) => [...nds, newNode]);
        setNodeCounter(nodeCounter + 1);
        setNodeValue('');
        setActionLog(prev => [...prev, { type: 'addNode', value }]);
    }, [nodeValue, nodeCounter, setNodes]);

    // Clear all
    const clearAll = useCallback(() => {
        setNodes([]);
        setEdges([]);
        setNodeCounter(1);
        setConnectingFrom(null);
        setNodeValue('');
        setActionLog(prev => [...prev, { type: 'clear' }]);
    }, [setNodes, setEdges]);

    // Generate code with actionLog
    const generateCode = useCallback(() => {

        const renderActions = (lang) => {
            return actionLog.map(action => {
                if (action.type === 'addNode') {
                    if (lang === 'javascript') return `myTree.addNode("${action.value}");`;
                    if (lang === 'python')     return `my_tree.add_node("${action.value}")`;
                    if (lang === 'java')       return `        myTree.addNode("${action.value}");`;
                    if (lang === 'cpp')        return `    myTree.addNode("${action.value}");`;
                }
                if (action.type === 'connect') {
                    if (lang === 'javascript') return `myTree.connectNodes("${action.from}", "${action.to}");`;
                    if (lang === 'python')     return `my_tree.connect_nodes("${action.from}", "${action.to}")`;
                    if (lang === 'java')       return `        myTree.connectNodes("${action.from}", "${action.to}");`;
                    if (lang === 'cpp')        return `    myTree.connectNodes("${action.from}", "${action.to}");`;
                }
                if (action.type === 'deleteNode') {
                    if (lang === 'javascript') return `myTree.removeNode("${action.value}");`;
                    if (lang === 'python')     return `my_tree.remove_node("${action.value}")`;
                    if (lang === 'java')       return `        myTree.removeNode("${action.value}");`;
                    if (lang === 'cpp')        return `    myTree.removeNode("${action.value}");`;
                }
                if (action.type === 'deleteEdge') {
                    if (lang === 'javascript') return `myTree.removeEdge("${action.from}", "${action.to}");`;
                    if (lang === 'python')     return `my_tree.remove_edge("${action.from}", "${action.to}")`;
                    if (lang === 'java')       return `        myTree.removeEdge("${action.from}", "${action.to}");`;
                    if (lang === 'cpp')        return `    myTree.removeEdge("${action.from}", "${action.to}");`;
                }
                if (action.type === 'clear') {
                    if (lang === 'javascript') return `// Tree was cleared at this point`;
                    if (lang === 'python')     return `# Tree was cleared at this point`;
                    if (lang === 'java')       return `        // Tree was cleared at this point`;
                    if (lang === 'cpp')        return `    // Tree was cleared at this point`;
                }
                return '';
            }).filter(Boolean).join('\n');
        };

        const codes = {
            javascript: `// Tree Implementation in JavaScript

class TreeNode {
    constructor(value) {
        this.value = value;
        this.children = [];
        this.parent = null;
    }

    addChild(child) {
        child.parent = this;
        this.children.push(child);
    }
}

class Tree {
    constructor() {
        this.nodes = new Map();
    }

    addNode(value) {
        const node = new TreeNode(value);
        this.nodes.set(value, node);
        return node;
    }

    connectNodes(parentValue, childValue) {
        const parent = this.nodes.get(parentValue);
        const child = this.nodes.get(childValue);
        if (parent && child) {
            parent.addChild(child);
        }
    }

    removeNode(value) {
        const node = this.nodes.get(value);
        if (!node) return;
        if (node.parent) {
            node.parent.children = node.parent.children.filter(c => c.value !== value);
        }
        node.children.forEach(child => child.parent = null);
        this.nodes.delete(value);
    }

    removeEdge(parentValue, childValue) {
        const parent = this.nodes.get(parentValue);
        const child = this.nodes.get(childValue);
        if (parent && child) {
            parent.children = parent.children.filter(c => c.value !== childValue);
            child.parent = null;
        }
    }
}

// Main
const myTree = new Tree();
${renderActions('javascript')}`,

            python: `# Tree Implementation in Python

class TreeNode:
    def __init__(self, value):
        self.value = value
        self.children = []
        self.parent = None

    def add_child(self, child):
        child.parent = self
        self.children.append(child)

class Tree:
    def __init__(self):
        self.nodes = {}

    def add_node(self, value):
        node = TreeNode(value)
        self.nodes[value] = node
        return node

    def connect_nodes(self, parent_value, child_value):
        parent = self.nodes.get(parent_value)
        child = self.nodes.get(child_value)
        if parent and child:
            parent.add_child(child)

    def remove_node(self, value):
        node = self.nodes.get(value)
        if not node:
            return
        if node.parent:
            node.parent.children = [c for c in node.parent.children if c.value != value]
        for child in node.children:
            child.parent = None
        del self.nodes[value]

    def remove_edge(self, parent_value, child_value):
        parent = self.nodes.get(parent_value)
        child = self.nodes.get(child_value)
        if parent and child:
            parent.children = [c for c in parent.children if c.value != child_value]
            child.parent = None

# Main
if __name__ == "__main__":
    my_tree = Tree()
${renderActions('python').split('\n').map(l => '    ' + l).join('\n')}`,

            cpp: `// Tree Implementation in C++
#include <iostream>
#include <vector>
#include <string>
#include <unordered_map>
#include <memory>
using namespace std;

class TreeNode : public enable_shared_from_this<TreeNode> {
public:
    string value;
    vector<shared_ptr<TreeNode>> children;
    weak_ptr<TreeNode> parent;

    TreeNode(const string& val) : value(val) {}

    void addChild(shared_ptr<TreeNode> child) {
        child->parent = shared_from_this();
        children.push_back(child);
    }
};

class Tree {
private:
    unordered_map<string, shared_ptr<TreeNode>> nodes;

public:
    shared_ptr<TreeNode> addNode(const string& value) {
        auto node = make_shared<TreeNode>(value);
        nodes[value] = node;
        return node;
    }

    void connectNodes(const string& parentValue, const string& childValue) {
        auto parent = nodes[parentValue];
        auto child = nodes[childValue];
        if (parent && child) {
            parent->addChild(child);
        }
    }

    void removeNode(const string& value) {
        auto it = nodes.find(value);
        if (it == nodes.end()) return;
        auto node = it->second;
        auto parentPtr = node->parent.lock();
        if (parentPtr) {
            auto& ch = parentPtr->children;
            ch.erase(remove_if(ch.begin(), ch.end(),
                [&](const shared_ptr<TreeNode>& c){ return c->value == value; }), ch.end());
        }
        for (auto& child : node->children) {
            child->parent.reset();
        }
        nodes.erase(it);
    }

    void removeEdge(const string& parentValue, const string& childValue) {
        auto parent = nodes[parentValue];
        auto child = nodes[childValue];
        if (parent && child) {
            auto& ch = parent->children;
            ch.erase(remove_if(ch.begin(), ch.end(),
                [&](const shared_ptr<TreeNode>& c){ return c->value == childValue; }), ch.end());
            child->parent.reset();
        }
    }
};

int main() {
    Tree myTree;
${renderActions('cpp')}
    return 0;
}`,

            java: `// Tree Implementation in Java
import java.util.*;

class TreeNode {
    private String value;
    private List<TreeNode> children;
    private TreeNode parent;

    public TreeNode(String value) {
        this.value = value;
        this.children = new ArrayList<>();
        this.parent = null;
    }

    public void addChild(TreeNode child) {
        child.parent = this;
        this.children.add(child);
    }

    public String getValue() { return value; }
    public List<TreeNode> getChildren() { return children; }
    public TreeNode getParent() { return parent; }
    public void setParent(TreeNode parent) { this.parent = parent; }
}

class Tree {
    private Map<String, TreeNode> nodes;

    public Tree() {
        this.nodes = new HashMap<>();
    }

    public TreeNode addNode(String value) {
        TreeNode node = new TreeNode(value);
        nodes.put(value, node);
        return node;
    }

    public void connectNodes(String parentValue, String childValue) {
        TreeNode parent = nodes.get(parentValue);
        TreeNode child = nodes.get(childValue);
        if (parent != null && child != null) {
            parent.addChild(child);
        }
    }

    public void removeNode(String value) {
        TreeNode node = nodes.get(value);
        if (node == null) return;
        if (node.getParent() != null) {
            node.getParent().getChildren().removeIf(c -> c.getValue().equals(value));
        }
        for (TreeNode child : node.getChildren()) {
            child.setParent(null);
        }
        nodes.remove(value);
    }

    public void removeEdge(String parentValue, String childValue) {
        TreeNode parent = nodes.get(parentValue);
        TreeNode child = nodes.get(childValue);
        if (parent != null && child != null) {
            parent.getChildren().removeIf(c -> c.getValue().equals(childValue));
            child.setParent(null);
        }
    }
}

public class Main {
    public static void main(String[] args) {
        Tree myTree = new Tree();
${renderActions('java')}
    }
}`
        };

        return codes[selectedLanguage] || '';
    }, [actionLog, selectedLanguage]);

    // Copy code to clipboard
    const copyToClipboard = useCallback(() => {
        const code = generateCode();
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    }, [generateCode]);

    const getStyledNodes = () => {
        return nodes.map(node => ({ ...node }));
    };

    return (
        <>
            <style>
                {`
                    .tree-builder-container {
                        display: flex;
                        min-height: 100vh;
                        background: ${darkMode ? '#0f172a' : 'var(--lightModeBack)'};
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        padding: 20px;
                    }

                    .tree-main-col {
                        background: ${darkMode ? '#1E293B' : 'var(--customBuildBack)'};
                        backdrop-filter: blur(20px);
                        border-radius: 24px;
                        padding: 30px;
                        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
                        border: 1px solid ${darkMode ? 'rgba(122, 142, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
                        width: 100%;
                    }

                    .tree-title {
                        font-size: 2.5rem;
                        font-weight: 800;
                        background: rgb(122, 142, 255);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                        text-align: center;
                        margin-bottom: 30px;
                        letter-spacing: -0.02em;
                    }

                    .tree-input-container {
                        display: flex;
                        gap: 12px;
                        align-items: flex-start;
                        margin-bottom: 30px;
                    }

                    .tree-input {
                        flex: 1;
                        padding: 12px 16px;
                        border: 2px solid #e5e7eb;
                        border-radius: 8px;
                        font-size: 0.9rem;
                        transition: border-color 0.2s ease;
                        outline: none;
                        min-width: 0;
                    }

                    .tree-input::placeholder {
                        color: #363a42ff;
                        opacity: 1;
                    }

                    .tree-hint-button {
                        padding: 16px 20px;
                        border-radius: 16px;
                        border: 2px solid #7a8effff;
                        font-size: 1rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        font-family: inherit;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        background: ${darkMode ? "#1e293b" : "var(--customBuildButton)"};
                        color: #7a8effff;
                        min-width: 56px;
                        height: 56px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                    }

                    .tree-functions-grid {
                        display: grid;
                        grid-template-columns: repeat(4, 1fr);
                        gap: 15px;
                        margin-bottom: 30px;
                    }

                    .tree-button {
                        padding: 16px 24px;
                        border-radius: 16px;
                        border: 2px solid #7a8effff;
                        font-size: 1rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        font-family: Poppins, sans-serif;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        background: ${darkMode ? "#1e293b" : "var(--customBuildButton)"};
                        color: #7a8effff;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        white-space: nowrap;
                    }

                    .tree-button.active {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        border: none;
                    }

                    .tree-button:hover {
                        transform: scale(1.05);
                    }

                    .tree-flow-container {
                        height: 550px;
                        width: 100%;
                        border: 2px solid #4B5563;
                        border-radius: 12px;
                        margin-bottom: 30px;
                    }

                    .tree-label {
                        font-size: 0.875rem;
                        font-weight: 600;
                        color: ${darkMode ? '#ffffffff' : "#0b0a0aff"};
                        margin-bottom: 8px;
                    }

                    .tree-select {
                        width: 100%;
                        padding: 12px 16px;
                        border: 2px solid #e5e7eb;
                        border-radius: 8px;
                        font-size: 0.9rem;
                        transition: border-color 0.2s ease;
                        outline: none;
                        margin-bottom: 24px;
                    }

                    .tree-code-display {
                        background: linear-gradient(145deg, #0f172a, #1e293b);
                        color: white;
                        padding: 16px;
                        border-radius: 8px;
                        font-family: 'Fira Code', 'Monaco', 'Cascadia Code', monospace;
                        font-size: 0.8rem;
                        line-height: 1.5;
                        overflow: auto;
                        max-height: 300px;
                        white-space: pre-wrap;
                        border: 1px solid #334155;
                    }

                    .tree-copy-button {
                        width: 100%;
                        margin-top: 20px;
                        padding: 16px 24px;
                        border-radius: 16px;
                        border: none;
                        font-size: 1rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                    }

                    .tree-tooltip {
                        position: absolute;
                        top: 100%;
                        left: 50%;
                        transform: translateX(-50%);
                        margin-top: 10px;
                        padding: 20px;
                        background: linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98));
                        backdrop-filter: blur(20px);
                        border-radius: 16px;
                        border: 1px solid rgba(122, 142, 255, 0.3);
                        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
                        z-index: 1000;
                        min-width: 280px;
                    }

                    @media (max-width: 1024px) {
                        .tree-builder-container { padding: 15px; }
                        .tree-main-col { padding: 20px; }
                        .tree-title { font-size: 2rem; margin-bottom: 20px; }
                        .tree-functions-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
                        .tree-flow-container { height: 400px; }
                        .tree-code-display { max-height: 250px; font-size: 0.75rem; }
                    }

                    @media (max-width: 768px) {
                        .tree-builder-container { padding: 10px; }
                        .tree-main-col { padding: 15px; border-radius: 16px; }
                        .tree-title { font-size: 1.5rem; margin-bottom: 15px; }
                        .tree-input-container { flex-direction: column; gap: 10px; margin-bottom: 20px; }
                        .tree-input { width: 100%; font-size: 0.85rem; padding: 10px 14px; }
                        .tree-hint-button { width: 100%; min-width: unset; height: 48px; padding: 12px 16px; }
                        .tree-functions-grid { grid-template-columns: 1fr; gap: 10px; margin-bottom: 20px; }
                        .tree-button { padding: 12px 16px; font-size: 0.9rem; width: 100%; }
                        .tree-flow-container { height: 300px; margin-bottom: 20px; }
                        .tree-label { font-size: 0.8rem; margin-bottom: 6px; }
                        .tree-select { font-size: 0.85rem; padding: 10px 14px; margin-bottom: 20px; }
                        .tree-code-display { max-height: 200px; font-size: 0.7rem; padding: 12px; }
                        .tree-copy-button { padding: 12px 16px; font-size: 0.9rem; margin-top: 15px; }
                        .tree-tooltip { min-width: 250px; padding: 15px; font-size: 0.85rem; }
                    }

                    @media (max-width: 480px) {
                        .tree-title { font-size: 1.25rem; }
                        .tree-flow-container { height: 250px; }
                        .tree-code-display { max-height: 150px; font-size: 0.65rem; }
                        .tree-tooltip { min-width: 220px; left: 0; right: 0; transform: translateX(0); margin: 10px; }
                    }
                `}
            </style>

            <div className="tree-builder-container">
                <Col className="tree-main-col">
                    <div>
                        <h1 className="tree-title">
                            {selLanguage ? "Δημιουργία Δέντρου" : "Tree Builder"}
                        </h1>

                        {/* Node Input */}
                        <div className="tree-input-container">
                            <input
                                type="text"
                                value={nodeValue}
                                onChange={(e) => setNodeValue(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && addNode()}
                                placeholder={selLanguage ? "Εισάγετε την τιμή του κόμβου..." : "Add the node value..."}
                                className="tree-input"
                            />

                            <div style={{ position: 'relative' }}>
                                <button
                                    className="tree-hint-button"
                                    onMouseOver={() => setShowInfo(true)}
                                    onMouseOut={() => setShowInfo(false)}
                                >
                                    💡
                                </button>
                                {showInfo && (
                                    <div
                                        className="tree-tooltip"
                                        onMouseEnter={() => setShowInfo(true)}
                                        onMouseLeave={() => setShowInfo(false)}
                                    >
                                        <div style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '0',
                                            height: '0',
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid rgba(30, 41, 59, 0.98)'
                                        }} />
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#ffffff', marginBottom: '15px', textAlign: 'center' }}>
                                            {selLanguage ? "Οδηγίες Χρήσης" : "Instructions"}
                                        </h3>
                                        <div style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                                            <div><strong>{selLanguage ? "Επιλογή & Μετακίνηση:" : "Select & Move:"}</strong> {selLanguage ? "Σύρετε & αποθέστε κόμβους" : "Drag & drop nodes"}</div>
                                            <div><strong>{selLanguage ? "Σύνδεση:" : "Connect:"}</strong> {selLanguage ? "Κλικ σε 2 κόμβους για σύνδεση" : "Click on 2 nodes to connect"}</div>
                                            <div><strong>{selLanguage ? "Διαγραφή:" : "Delete:"}</strong> {selLanguage ? "Κλικ για διαγραφή" : "Click to delete"}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tree-label">
                            {selLanguage ? "Λειτουργίες" : "Functions"}
                        </div>

                        <div className="tree-functions-grid">
                            <button onClick={addNode} className="tree-button">
                                <Plus size={16} />
                                <span>{selLanguage ? "Προσθήκη Κόμβου" : "Add Node"}</span>
                            </button>

                            <button
                                onClick={() => { setMode('connect'); setConnectingFrom(null); }}
                                className={`tree-button ${mode === 'connect' ? 'active' : ''}`}
                            >
                                <Target size={16} />
                                <span>{selLanguage ? "Σύνδεση" : "Connect"} {connectingFrom ? '(1/2)' : ''}</span>
                            </button>

                            <button
                                onClick={() => { setMode('delete'); setConnectingFrom(null); }}
                                className={`tree-button ${mode === 'delete' ? 'active' : ''}`}
                            >
                                <Trash2 size={16} />
                                <span>{selLanguage ? "Διαγραφή" : "Delete"}</span>
                            </button>

                            <button onClick={clearAll} className="tree-button">
                                <RotateCcw size={16} />
                                <span>{selLanguage ? "Καθαρισμός" : "Restart"}</span>
                            </button>
                        </div>

                        <div className="tree-flow-container">
                            <ReactFlow
                                nodes={getStyledNodes()}
                                edges={edges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onNodeClick={onNodeClick}
                                onEdgeClick={onEdgeClick}
                                nodeTypes={nodeTypes}
                                connectionMode={ConnectionMode.Loose}
                                fitView
                                defaultEdgeOptions={{
                                    type: 'default',
                                    markerEnd: { type: MarkerType.ArrowClosed },
                                    style: { strokeWidth: 2, stroke: '#667eea' }
                                }}
                                style={{
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)',
                                    borderRadius: '12px',
                                }}
                            >
                                <Controls />
                                <Background variant="dots" gap={20} size={1} color="#cbd5e1" />
                            </ReactFlow>
                        </div>

                        <div>
                            <br />
                            <label className="tree-label">
                                {selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}
                            </label>
                            <select
                                value={selectedLanguage}
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                className="tree-select"
                            >
                                <option value="javascript">JavaScript</option>
                                <option value="python">Python</option>
                                <option value="cpp">C++</option>
                                <option value="java">Java</option>
                            </select>

                            <label className="tree-label">{selLanguage ? "Παραγόμενος Κώδικας" : "Generated code"}</label>
                            <div className="tree-code-display">
                                <CodeHighlighter code={generateCode()} language={selectedLanguage} />
                            </div>
                            <button
                                className='tree-copy-button'
                                onClick={copyToClipboard}
                                style={{
                                    background: copied
                                        ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: 'white',
                                    transform: copied ? 'translateY(-4px)' : 'translateY(0)',
                                }}
                                onMouseOver={(e) => !copied && (e.target.style.transform = 'translateY(-2px)')}
                                onMouseOut={(e) => !copied && (e.target.style.transform = 'translateY(0)')}
                            >
                                <Copy size={16} />
                                <span>{copied ? (selLanguage ? 'Αντιγράφηκε!' : 'Copied') : (selLanguage ? 'Αντιγραφή' : 'Copy')}</span>
                            </button>
                        </div>
                    </div>
                </Col>
            </div>
        </>
    );
};

export default TreeBuilder;